/**
 * Universe API client (bd deepcontext-d68.6).
 *
 * Typed wrappers for the Universe endpoints introduced in d68.4 and extended
 * in d68.7 / d68.8:
 *   GET  /api/clusters?market=<lang>      — assembled payload
 *   GET  /api/clusters/topics/:id         — topic drill-down
 *   POST /api/clusters/score              — score a free-form brief
 *   GET  /api/clusters/urls               — server-side URL Explorer (d68.7)
 *   GET  /api/clusters/trending           — velocity-ranked clusters (d68.8)
 *
 * Wraps the configured axios instance from AuthContext (carries auth + refresh
 * interceptors). Response shapes come from the canonical wire contract
 * (backend/src/types/wire.ts) shared with the backend service interfaces.
 */

import {api} from '../contexts/AuthContext';
import type {
    ClustersPayload,
    ClustersScoringResult,
    ClustersStoryMember,
    ClustersTopicDetail,
    ClustersUrlRow,
    ClustersUrlsResult,
    StoryFitScore,
    TopMentionEntity,
    TopMentionSampleUrl,
    TopMentionSentimentBreakdown,
    TopMentionsPayload,
    TrendingClusterPayload,
    TrendingClusterTopicPayload,
    TrendingPayload,
    TrendingStoryPayload,
} from '../types';

export type {
    ClustersPayload,
    ClustersScoringResult,
    ClustersStoryMember,
    ClustersTopicDetail,
    ClustersUrlRow,
    StoryFitScore,
    TopMentionEntity,
    TopMentionSampleUrl,
    TopMentionSentimentBreakdown,
    TopMentionsPayload,
    TrendingClusterPayload,
    TrendingClusterTopicPayload,
    TrendingPayload,
    TrendingStoryPayload,
};

/** Row in the URL Explorer table (bd deepcontext-d68.7); alias of the wire `ClustersStoryMember` shape. */
export type ClustersTopUrlEntry = ClustersStoryMember;

/** Response shape from GET /api/clusters/urls. */
export type ClustersUrlsResponse = ClustersUrlsResult;

export interface FetchClustersUrlsParams {
    market: string;
    clusterId?: string | null;
    q?: string | null;
    limit?: number;
    offset?: number;
    /** `'recent'` orders by `joined_at DESC`; omitted keeps the default match-score order. */
    sort?: 'recent';
}

/**
 * Per-call axios options accepted by every method on `clustersApi`.
 *
 * Callers pass `{signal}` from an `AbortController` so a route change or
 * unmount can cancel in-flight Universe XHRs (bd deepcontext-26s). The signal
 * is forwarded straight to axios — without it, requests run to completion and
 * either waste bandwidth or risk a last-writer-wins setState on a stale tab.
 *
 * Cancellation surfaces in the caller as a rejection where
 * `err.name === 'AbortError'` (jsdom / native fetch path) or
 * `err.code === 'ERR_CANCELED'` (axios). Match the bd-5jt / bd-sqi precedent
 * in callers when filtering them out.
 */
export interface ClustersApiOptions {
    signal?: AbortSignal;
}

export const clustersApi = {
    /** GET /api/clusters?market=<lang> — assembled Universe payload. */
    getClusters: async (
        market: string,
        options: ClustersApiOptions = {},
    ): Promise<ClustersPayload> => {
        const response = await api.get('/api/clusters', {
            params: {market},
            signal: options.signal,
        });
        return response.data as ClustersPayload;
    },

    /** GET /api/clusters/topics/:id — full topic + parent cluster + top URLs. */
    getTopic: async (
        topicId: string,
        options: ClustersApiOptions = {},
    ): Promise<ClustersTopicDetail> => {
        const response = await api.get(
            `/api/clusters/topics/${encodeURIComponent(topicId)}`,
            {signal: options.signal},
        );
        return response.data as ClustersTopicDetail;
    },

    /** POST /api/clusters/score — body: { brief, market } — fit scores per cluster. */
    scoreBrief: async (
        brief: string,
        market: string,
        options: ClustersApiOptions = {},
    ): Promise<ClustersScoringResult> => {
        const response = await api.post(
            '/api/clusters/score',
            {brief, market},
            {signal: options.signal},
        );
        return response.data as ClustersScoringResult;
    },

    /**
     * POST /api/clusters/score — body: { blueprintId, market } — anchor the
     * bubble map to an existing Brand Blueprint (deepcontext-iasew). Same
     * endpoint + response shape as `scoreBrief`; the backend swaps the
     * embedding source (the blueprint's cached `content_embedding`, encoded on
     * demand if empty) and reuses the identical HNSW → per-cluster fit path.
     */
    scoreBlueprint: async (
        blueprintId: string,
        market: string,
        options: ClustersApiOptions = {},
    ): Promise<ClustersScoringResult> => {
        const response = await api.post(
            '/api/clusters/score',
            {blueprintId, market},
            {signal: options.signal},
        );
        return response.data as ClustersScoringResult;
    },

    /** GET /api/clusters/urls — server-side filter + pagination across URLs. */
    fetchClustersUrls: async (
        params: FetchClustersUrlsParams,
        options: ClustersApiOptions = {},
    ): Promise<ClustersUrlsResponse> => {
        const query: Record<string, string | number> = {market: params.market};
        if (params.clusterId) query.cluster = params.clusterId;
        if (params.q) query.q = params.q;
        if (typeof params.limit === 'number') query.limit = params.limit;
        if (typeof params.offset === 'number') query.offset = params.offset;
        if (params.sort) query.sort = params.sort;
        const response = await api.get('/api/clusters/urls', {
            params: query,
            signal: options.signal,
        });
        return response.data as ClustersUrlsResponse;
    },

    /** GET /api/clusters/trending?market=<lang> — velocity-ranked clusters (d68.8). */
    getTrending: async (
        market: string,
        options: ClustersApiOptions = {},
    ): Promise<TrendingPayload> => {
        const response = await api.get('/api/clusters/trending', {
            params: {market},
            signal: options.signal,
        });
        return response.data as TrendingPayload;
    },

    /** GET /api/clusters/mentions?market&limit — Top Mentions bubbles (d68.9). */
    getTopMentions: async (
        market: string,
        limit: number = 50,
        options: ClustersApiOptions = {},
    ): Promise<TopMentionsPayload> => {
        const response = await api.get('/api/clusters/mentions', {
            params: {market, limit},
            signal: options.signal,
        });
        return response.data as TopMentionsPayload;
    },
};

/**
 * Returns true when a rejection originated from an `AbortController.abort()`
 * call (axios cancellation OR native fetch). Use in `.catch` handlers to
 * swallow cancellation without logging a spurious error (bd deepcontext-26s).
 *
 * We don't import `axios.isCancel` because the shared client at
 * `contexts/AuthContext` doesn't re-export it, and matching by `name` /
 * `code` is the style already used elsewhere in the codebase (bd-5jt,
 * bd-sqi).
 */
export function isAbortError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    const e = err as {name?: string; code?: string};
    return e.name === 'AbortError' || e.name === 'CanceledError' || e.code === 'ERR_CANCELED';
}

/**
 * Extracts a user-facing error message from a failed API call, preferring
 * the backend's crafted `error`/`message` body over the generic Axios
 * status-line message (e.g. "Request failed with status code 429" masking
 * the rate-limit copy from `backend/src/routes/clusters.ts`).
 */
export function extractApiError(err: unknown, fallback: string): string {
    const e = err as {
        response?: {data?: {error?: string; message?: string}};
        message?: string;
    } | null;
    return (
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        fallback
    );
}
